/**
 * SecureFileOpenProvider — SDE Phase 3 (s88).
 *
 * Orchestrates opening the encrypted .oesif Secure File back into the in-memory store:
 *   - Chrome/Edge: a persisted handle (one-click permission) → else showOpenFilePicker.
 *   - Safari/Firefox: a hidden <input type="file">.
 * Tries NO_PASSPHRASE_KEY first; on a passphrase-protected file it prompts for the
 * passphrase and retries. On success it loadValues() into the store, marks setup complete
 * (handle + passphrase), and persists the handle (IndexedDB) for next time.
 *
 * Auto-reconnect on mount is SILENT: browser requestPermission() needs a user gesture, so
 * we only auto-load when permission is already granted (queryPermission). Otherwise the
 * planner's "Open file" click (a gesture) surfaces the one-click permission prompt.
 *
 * Must be mounted inside SensitiveDataSessionProvider.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { supportsFileSystemAccess, FILE_EXTENSION } from "@/lib/secure-file";
import { NO_PASSPHRASE_KEY } from "@/lib/secure-file-save";
import { readHandleBytes, readFileBytes, parseSecureFile, classifyOpenError } from "@/lib/secure-file-open";
import { saveHandle, getHandle, clearHandle, ensureHandlePermission } from "@/lib/secure-file-handle-store";
import { PRODUCT_NAME } from "@shared/brand";

interface SecureFileOpenContextValue {
  /** Trigger the open flow (from an "Open file" button — a user gesture). */
  openFile: () => void;
}

const Ctx = createContext<SecureFileOpenContextValue | null>(null);

const NOOP_OPEN: SecureFileOpenContextValue = { openFile: () => {} };

/** Returns the open-flow API, or a no-op when no provider is mounted (e.g. unit tests /
 *  surfaces without the secure-file flow). Never throws — the "Open file" affordance just
 *  becomes inert rather than crashing the field render. */
export function useSecureFileOpen(): SecureFileOpenContextValue {
  return useContext(Ctx) ?? NOOP_OPEN;
}

interface PassState {
  open: boolean;
  bytes: Uint8Array | null;
  handle: FileSystemFileHandle | null;
  passphrase: string;
  error: string | null;
  busy: boolean;
}
const INITIAL_PASS: PassState = { open: false, bytes: null, handle: null, passphrase: "", error: null, busy: false };

export function SecureFileOpenProvider({ children }: { children: React.ReactNode }) {
  const session = useSensitiveDataSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pass, setPass] = useState<PassState>(INITIAL_PASS);

  const finishLoad = useCallback(
    (values: Record<string, string>, count: number, handle: FileSystemFileHandle | null, passphrase: string, silent = false) => {
      sessionRef.current.loadValues(values);
      sessionRef.current.completeSetup(handle, passphrase);
      if (handle) void saveHandle(handle);
      if (!silent) toast({ title: `Secure file loaded — ${count} value${count === 1 ? "" : "s"} restored`, duration: 3500 });
    },
    [toast],
  );

  const errorToast = useCallback((kind: ReturnType<typeof classifyOpenError>) => {
    const description =
      kind === "wrong-file"
        ? `This doesn't look like an ${PRODUCT_NAME} secure file. Check the file and try again.`
        : kind === "corrupt"
        ? "This file couldn't be opened — it may be damaged. Try another copy if you have one."
        : "Please try again.";
    toast({ title: "Couldn't open the file", description, variant: "destructive" });
  }, [toast]);

  /** Decrypt + load. `fromModal` controls whether a passphrase failure re-prompts or
   *  shows the inline modal error. */
  const tryDecrypt = useCallback(
    async (bytes: Uint8Array, handle: FileSystemFileHandle | null, passphrase: string, fromModal: boolean) => {
      try {
        const parsed = await parseSecureFile(bytes, passphrase);
        finishLoad(parsed.values, parsed.count, handle, passphrase);
        setPass(INITIAL_PASS);
      } catch (err) {
        const kind = classifyOpenError(err);
        if (kind === "wrong-passphrase") {
          if (fromModal) {
            setPass((p) => ({ ...p, busy: false, error: "Passphrase didn't match. Check the note the planner left with their will." }));
          } else {
            // First attempt with NO_PASSPHRASE_KEY failed → the file is passphrase-protected.
            setPass({ open: true, bytes, handle, passphrase: "", error: null, busy: false });
          }
          return;
        }
        errorToast(kind);
        setPass(INITIAL_PASS);
      }
    },
    [finishLoad, errorToast],
  );

  const openFile = useCallback(async () => {
    try {
      if (supportsFileSystemAccess()) {
        const stored = await getHandle();
        if (stored && (await ensureHandlePermission(stored))) {
          try {
            const bytes = await readHandleBytes(stored);
            await tryDecrypt(bytes, stored, NO_PASSPHRASE_KEY, false);
            return;
          } catch {
            await clearHandle(); // stale handle → fall through to the picker
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const [handle] = (await (window as any).showOpenFilePicker({
          types: [{ description: `${PRODUCT_NAME} Secure Information File`, accept: { "application/octet-stream": [FILE_EXTENSION] } }],
          multiple: false,
        })) as FileSystemFileHandle[];
        await ensureHandlePermission(handle);
        const bytes = await readHandleBytes(handle);
        await tryDecrypt(bytes, handle, NO_PASSPHRASE_KEY, false);
        return;
      }
      // Safari/Firefox → file input
      fileInputRef.current?.click();
    } catch (err: any) {
      if (err?.name === "AbortError") return; // user cancelled the picker
      toast({ title: "Couldn't open the file", description: "Please try again.", variant: "destructive" });
    }
  }, [tryDecrypt, toast]);

  const onFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      const bytes = await readFileBytes(file);
      await tryDecrypt(bytes, null, NO_PASSPHRASE_KEY, false);
    },
    [tryDecrypt],
  );

  const submitPassphrase = useCallback(() => {
    if (!pass.bytes) return;
    setPass((p) => ({ ...p, busy: true, error: null }));
    void tryDecrypt(pass.bytes, pass.handle, pass.passphrase, true);
  }, [pass.bytes, pass.handle, pass.passphrase, tryDecrypt]);

  // Silent auto-reconnect on mount (Chrome/Edge, no-passphrase files with a standing grant).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supportsFileSystemAccess()) return;
      const handle = await getHandle();
      if (!handle || cancelled) return;
      const granted = await ensureHandlePermission(handle, { silent: true });
      if (!granted || cancelled) return; // needs a gesture → wait for the "Open file" click
      try {
        const bytes = await readHandleBytes(handle);
        const parsed = await parseSecureFile(bytes, NO_PASSPHRASE_KEY);
        if (!cancelled) finishLoad(parsed.values, parsed.count, handle, NO_PASSPHRASE_KEY, true);
      } catch {
        // Passphrase-protected (can't decrypt silently) or unreadable → leave for manual open.
      }
    })();
    return () => { cancelled = true; };
  }, [finishLoad]);

  return (
    <Ctx.Provider value={{ openFile }}>
      {children}
      <input ref={fileInputRef} type="file" accept={FILE_EXTENSION} className="hidden" onChange={onFileInputChange} data-testid="secure-file-input" />
      <Dialog open={pass.open} onOpenChange={(o) => !o && setPass(INITIAL_PASS)}>
        <DialogContent className="bg-[#FEFCF8] border border-[#D9D1C1] rounded-2xl max-w-md" data-testid="secure-file-passphrase-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[#4F6B51]">
              <Lock className="h-5 w-5 shrink-0" />
              <DialogTitle className="font-['Fraunces'] font-medium text-lg text-[#2B2A26]">This file is passphrase-protected</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#2B2A26]/80 pt-1">
              Enter the passphrase the planner set when they created this file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="password"
              value={pass.passphrase}
              onChange={(e) => setPass((p) => ({ ...p, passphrase: e.target.value, error: null }))}
              onKeyDown={(e) => { if (e.key === "Enter" && pass.passphrase) submitPassphrase(); }}
              placeholder="Passphrase"
              autoFocus
              className="bg-[#FAF7F1] border-[#D9D1C1] text-[#2B2A26] rounded-lg font-mono text-sm"
              data-testid="secure-file-passphrase-input"
            />
            {pass.error && <p className="text-xs text-red-600" data-testid="secure-file-passphrase-error">{pass.error}</p>}
            <div className="flex gap-3 pt-1">
              <Button onClick={submitPassphrase} disabled={pass.busy || !pass.passphrase} className="flex-1 bg-[#4F6B51] hover:bg-[#4F6B51]/90 text-white rounded-lg disabled:opacity-50" data-testid="secure-file-passphrase-submit">
                {pass.busy ? "Opening…" : "Open file"}
              </Button>
              <button type="button" onClick={() => setPass(INITIAL_PASS)} className="text-sm text-[#2B2A26]/60 hover:text-[#2B2A26]">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}
